import {Component, OnInit} from '@angular/core';
import {WidgetData} from '../../../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../../../core/shared/widget/service/widget.factory';
import {WidgetDataParam} from '../../../../../core/shared/widget/widget-data-param';
import {Sorting} from '../../../../../core/shared/misc/sorting';

/**
 * Tab descriptor for the unified Side-Car Playground shell. `key` is the stable
 * tab value bound to `p-tabs`; `label`/`icon` are the German tab-bar display.
 *
 * @ticket TM-1000001960
 */
interface XxPlaygroundTab {
    /** Stable tab value (bound to `p-tab` / `p-tabpanel`). */
    key: string;
    /** German tab-bar label. */
    label: string;
    /** FontAwesome icon class for the tab. */
    icon: string;
}

/**
 * Unified "Side-Car Playground" shell (TM-1000001960).
 *
 * Merges the previously separate xx playground surfaces into ONE route
 * (`xx/playground`) with a tab bar:
 * - Tab 1 "QL-Demos": embeds the existing {@link XxSidecarPlaygroundComponent}
 *   (`<mvs-xx-sidecar-playground>`) — the three QL demo cards (#4 / #5 / #3).
 * - Tab 2 "Widgets": the three self-contained side-car display widgets
 *   (`<mvs-xx-person-external-flat>`, `<mvs-xx-person-external-typed>`,
 *   `<mvs-xx-person-data>`) plus ONE framework-widget example (`<mvs-widget>`
 *   over a QL table on `xx.XxPerson`) to demonstrate the WidgetFactory path.
 * - Tab 3 "uf-Erfassung": the {@link XxPlaygroundUfComponent}
 *   (`<mvs-xx-playground-uf>`) — runs a real uf flow that materializes the
 *   side-cars for a chosen person.
 * - Tab 4 "Berufe (TAA)": the {@link XxPlaygroundBerufeComponent}
 *   (`<mvs-xx-playground-berufe>`) — edits the INTERNAL typed side-car of a
 *   chosen person through the generic CRUD form, so the occupation field is
 *   rendered by the framework's search-backed value list control
 *   (`uiInputControl = "valueListSearch"`) over the ~6.333-entry Stuttgarter
 *   TAA Berufeliste.
 *
 * Uses the PrimeNG 20 tabs API (`p-tabs` / `p-tablist` / `p-tab` /
 * `p-tabpanels` / `p-tabpanel`) — the same tab component the codebase already
 * uses (e.g. bm-customer-billing-dashboard) and available transitively via CoreModule.
 *
 * @ticket TM-1000001960
 */
@Component({
    selector: 'mvs-xx-playground-shell',
    templateUrl: './xx-playground-shell.component.html',
    styleUrls: ['./xx-playground-shell.component.scss'],
    standalone: false
})
export class XxPlaygroundShellComponent implements OnInit {

    /** True once the shell (and its framework widget) is initialized. */
    initialized = false;

    /** Tab bar definition (German labels, stable keys). */
    readonly tabs: XxPlaygroundTab[] = [
        {key: 'ql-demos', label: 'QL-Demos', icon: 'fa-light fa-database'},
        {key: 'widgets', label: 'Widgets', icon: 'fa-light fa-table-cells'},
        {key: 'uf', label: 'uf-Erfassung', icon: 'fa-light fa-list-check'},
        // search-backed value list demo: edits the internal typed side-car of a person, whose
        // occupation field is served by the framework's `valueListSearch` control
        {key: 'berufe', label: 'Berufe (TAA)', icon: 'fa-light fa-briefcase'}
    ];

    /** Currently active tab key (first tab by default). */
    activeTab: string = this.tabs[0].key;

    /** Framework-widget example: a QL table over `xx.XxPerson` (Tab 2). */
    personTableWidgetData: WidgetData;

    ngOnInit(): void {
        this.initComponent();
    }

    /**
     * Initialize Component.
     *
     * @ticket TM-1000001960
     */
    initComponent(): void {
        this.refreshComponent();
    }

    /**
     * Refresh Component: build the framework-widget example.
     *
     * @ticket TM-1000001960
     */
    refreshComponent(): void {
        this.initWidgets();
        this.initialized = true;
    }

    /**
     * Build the framework-widget example for Tab 2: a QL-backed table over the
     * `xx.XxPerson` host entity, demonstrating the WidgetFactory + `<mvs-widget>`
     * data-provider path next to the self-contained side-car widgets.
     *
     * @ticket TM-1000001960
     */
    initWidgets(): void {
        this.personTableWidgetData = WidgetFactory.createWidgetTableEntityQl(
            'xx.playground.person.table',
            'Personen',
            'xx.XxPerson',
            'Keine Personen vorhanden.',
            [],
            [new Sorting('e.lastName', true)],
            false,
            WidgetDataParam.create('size', 'S')
        );
    }

    /**
     * Tab-change handler: store the newly selected tab key.
     *
     * @ticket TM-1000001960
     */
    onTabChange(value: string): void {
        this.activeTab = value;
    }
}
